import * as React from 'react';

import type {
  NegotiationEntryStateDto,
  NegotiationHistoryFilterDto,
  NegotiationLineDto,
} from '@/api/negotiation-client';
import {
  useLockNegotiation,
  useLockNegotiationSelection,
  useNegotiationComparison,
  useNegotiationLines,
} from '@/api/use-negotiation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FilterCombo } from '@/components/ui/filter-combo';
import { Label } from '@/components/ui/label';
import { Panel } from '@/components/ui/panel';
import { filterTriggerLabel } from '@/lib/filter-trigger-label';
import {
  NEGOTIATION_STATE_BADGE_CLASS,
  NEGOTIATION_STATE_LABEL,
  NEGOTIATION_STATE_ORDER,
  buildIndicators,
  buildProgress,
  coupleLabel,
  distinctSirets,
  filterLines,
  formatDateTimeFr,
  formatDayTimeFr,
  formatEurAmount,
  formatSignedEur,
  isAwaitingOver48h,
  lineKey,
  plural,
  profileOptions,
  supplierLabelOf,
  supplierOptions,
} from '@/lib/negotiation-view';
import { formatEur, seniorityLabel } from '@/lib/operational-view-labels';
import { cn } from '@/lib/utils';
import { NegotiationHistoryPanel } from '@/routes/admin/campaigns/negotiation-history-panel';
import {
  PanelFooter,
  PanelMessage,
  PanelToolbar,
} from '@/routes/admin/operational-view/operational-view-table';

// =============================================================================
// negotiation-screen.tsx — Page ADMIN « Négociation » REFONDUE (lot N4).
// =============================================================================
// Spec : `tasks/spec-refonte-negociation-20260917.md` (décisions GATE_HUMAN du 2026-09-17, précision du
// 2026-09-18 sur le modèle de verrouillage), DBU v2.9 §2.5, UX §8.6, maquette 2026-09-17.
//
// DISPOSITION (maquette) : ligne de 4 indicateurs sans card + bouton « Verrouiller toutes les ouvertes »,
// puis DEUX colonnes — la liste des négociations à gauche, l'historique FIXE à droite (empilés sous
// 1280 px). Les 10 colonnes du tableau tiennent dans la largeur de contenu d'un viewport de 1280 px
// (1280 − 240 px de rail − 56 px de gouttière = 984 px, moins la colonne d'historique et sa gouttière) :
// aucun défilement horizontal du conteneur.
//
// CE QUE FAIT L'ÉCRAN, ET RIEN DE PLUS :
//   • UNE lecture (`useNegotiationLines`, lot N1) rend toute la liste au grain Fournisseur × Profil ×
//     Séniorité — elle remplace l'enchaînement `getAuthState` + N × `getNegotiationComparison` ;
//   • UNE lecture (`useNegotiationHistory`, lot N3) rend la colonne droite, filtrée et paginée SERVEUR ;
//   • le verrouillage passe par `lockSelection` (lot N2), qui pré-vérifie le lot ENTIER avant d'écrire.
// AUCUNE règle métier : écart, état, valeur figée, tri et périmètre de l'historique sont l'autorité API.
//
// GRAIN DU VERROUILLAGE (précision GATE_HUMAN du 2026-09-18, verbatim : « La soumission de la grille
// bloque la saisie de tous les niveaux de séniorité ») : le tableau AFFICHE des lignes, le bouton AGIT
// sur le FOURNISSEUR. Tous les libellés d'action et toutes les confirmations nomment le fournisseur ET
// le nombre de lignes qui basculent — un bouton « Verrouiller » posé sur une ligne ne doit jamais
// laisser croire qu'il ne verrouille que cette ligne.
//
// OUVERTURE : le formulaire « Rouvrir la saisie » est SUPPRIMÉ (décision 2). L'ouverture se fait depuis
// la VUE OPÉRATIONNELLE, page Fournisseurs, dans le détail du fournisseur (décision GATE_HUMAN du
// 2026-09-22 : « il faut déplacer seulement la fonctionnalité négociation sur la page
// […]/operational-view/fournisseurs ») ; la page le rappelle là où le formulaire se trouvait. Le
// renvoi désignait l'onglet Analyse jusqu'à cette date : il a suivi le déplacement, sans quoi il
// enverrait l'admin sur un écran qui n'offre plus l'action.
//
// ÉTAT DE VUE : filtres, case « Afficher les verrouillées », onglet et page de l'historique vivent dans
// l'URL (RULE_REF_006 — aucun localStorage). Seuls la sélection de lignes et le dépliage du détail sont
// de l'état de session in-memory.
//
// WCAG 2.1 AA : <table>/<caption sr-only>/<th scope>, cases nommées, badge d'état DOUBLÉ d'un libellé
// texte, écart signé + libellé `sr-only` (jamais la couleur seule), modales de confirmation, messages
// role="status" / role="alert".

/** Colonne du tableau : id stable, libellé, largeur (px) et alignement. */
interface NegotiationColumn {
  readonly id: string;
  readonly label: string;
  readonly width: number;
  readonly numeric?: boolean;
}

/** Largeur de la colonne de sélection (case à cocher, sans libellé visible). */
const SELECTION_WIDTH = 26;

/** Les 10 colonnes de la maquette, dans l'ordre d'affichage. */
const COLUMNS: readonly NegotiationColumn[] = [
  { id: 'fournisseur', label: 'Fournisseur', width: 94 },
  { id: 'profil', label: 'Profil', width: 58 },
  { id: 'seniorite', label: 'Séniorité', width: 54 },
  { id: 'tjmC2', label: 'TJM C2', width: 50, numeric: true },
  { id: 'resaisi', label: 'Re-saisi', width: 50, numeric: true },
  { id: 'ecart', label: 'Écart', width: 52, numeric: true },
  { id: 'prixCible', label: 'Prix cible', width: 50, numeric: true },
  { id: 'ouverteLe', label: 'Ouverte le', width: 58 },
  // « Modification autorisée » / « En cours de modification » ne tiennent pas sur une ligne : le badge
  // RETOURNE À LA LIGNE dans cette colonne (sans quoi il débordait sur le bouton « Verrouiller »,
  // constaté le 2026-09-18). La largeur est celle d'un badge sur deux lignes, pas d'un libellé entier.
  { id: 'etat', label: 'État', width: 92 },
  { id: 'action', label: 'Action', width: 56 },
];

/** Largeur TOTALE de la table (somme des colonnes) : tient à 1280 px à côté de l'historique. */
const TABLE_WIDTH =
  SELECTION_WIDTH + COLUMNS.reduce((total, column) => total + column.width, 0);

const CELL = 'px-1.5 py-2 align-top text-xs';
const TEXT_CELL = `${CELL} truncate`;
const NUM_CELL = `${CELL} whitespace-nowrap text-right tabular-nums`;
const HEAD_CELL = 'px-1.5 py-2 align-bottom text-[10px] tracking-normal';

/** Id STABLE du panneau de détail (cible d'`aria-controls` des boutons « Détail »). */
const DETAIL_PANEL_ID = 'negotiation-detail-panel';

/**
 * Phrase d'EFFET du verrouillage, répétée à l'identique dans les trois confirmations (ligne, sélection,
 * global) : l'utilisateur doit lire la même règle quel que soit le bouton qu'il a pressé.
 */
const LOCK_EFFECT_SENTENCE =
  'La valeur re-saisie est retenue quand elle existe, sinon le TJM C2 est conservé. L’action est définitive et journalisée dans Audit.';

/**
 * Cellule « Écart » : signe EXPLICITE + flèche + libellé lisible par lecteur d'écran. L'information
 * n'est JAMAIS portée par la couleur seule (WCAG 1.4.1 / ST-NFR-03) — la couleur (vert si négatif,
 * c'est-à-dire favorable à l'acheteur) n'est qu'un renfort. `null` ⇒ « — ».
 */
function EcartCell({ ecart }: { readonly ecart: number | null }): React.JSX.Element {
  if (ecart === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (ecart === 0) {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-muted-foreground">
        <span aria-hidden="true">→</span>
        <span>{formatSignedEur(0)}</span>
        <span className="sr-only">inchangé</span>
      </span>
    );
  }
  const isUp = ecart > 0;
  const magnitude = formatEurAmount(Math.abs(ecart));
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-medium',
        isUp ? 'text-rose-700' : 'text-emerald-700',
      )}
    >
      <span aria-hidden="true">{isUp ? '↑' : '↓'}</span>
      <span>{formatSignedEur(ecart)}</span>
      <span className="sr-only">
        {isUp ? `en hausse de ${magnitude}` : `en baisse de ${magnitude}`}
      </span>
    </span>
  );
}

/** Un indicateur de la ligne de tête (libellé mono en capitales + valeur, sous-texte optionnel). */
function Indicator({
  label,
  value,
  hint,
  emphasis,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: React.ReactNode;
  /** `true` ⇒ valeur en ambre (renfort d'attention ; le libellé et le sous-texte portent le sens). */
  readonly emphasis?: boolean;
}): React.JSX.Element {
  return (
    <div className="min-w-0">
      <span className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'block text-3xl font-medium tabular-nums',
          emphasis === true ? 'text-dash-critical-fg' : '',
        )}
      >
        {value}
      </span>
      {hint === undefined ? null : (
        <span className="block text-xs text-muted-foreground">{hint}</span>
      )}
    </div>
  );
}

/**
 * Panneau « Détail » d'un fournisseur (comparaison avant/après, niveau par niveau). CONSERVÉ de
 * l'écran précédent : c'est le seul endroit où une négociation verrouillée se relit par niveau.
 */
function NegotiationDetailPanel({
  campaignId,
  supplierSiret,
  supplierLabel,
}: {
  readonly campaignId: string;
  readonly supplierSiret: string;
  readonly supplierLabel: string;
}): React.JSX.Element {
  const query = useNegotiationComparison(campaignId, supplierSiret, { enabled: true });

  return (
    <Panel
      headingId="negotiation-detail-heading"
      title={`Détail — ${supplierLabel}`}
      aside={<span>Comparaison avant / après, niveau par niveau</span>}
    >
      {query.isPending ? (
        <PanelMessage kind="status">Chargement du détail…</PanelMessage>
      ) : query.isError ? (
        <PanelMessage kind="alert">
          Le détail n’a pas pu être chargé : {query.error.message}
        </PanelMessage>
      ) : query.data.cells.length === 0 ? (
        <PanelMessage kind="status">
          Aucune cellule figée pour ce fournisseur.
        </PanelMessage>
      ) : (
        <div className="overflow-x-auto">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Détail de la négociation : TJM avant et après, par profil et niveau, avec l’écart.
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="px-6 py-2 text-left">
                  Profil
                </th>
                <th scope="col" className="px-3 py-2 text-left">
                  Niveau
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  TJM C2
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  Re-saisi
                </th>
                <th scope="col" className="px-6 py-2 text-right">
                  Écart
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.cells.map((cell) => (
                <tr
                  key={`${cell.profileCode}::${cell.seniorityLevel}`}
                  className="border-b border-border last:border-0"
                >
                  <th scope="row" className="px-6 py-2 text-left font-medium">
                    {cell.profileCode}
                  </th>
                  <td className="px-3 py-2">{cell.seniorityLevel}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatEur(cell.tjmBefore)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatEur(cell.tjmAfter)}
                  </td>
                  <td className="px-6 py-2 text-right tabular-nums">
                    <EcartCell ecart={cell.delta} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PanelFooter>
        Date de modification effective : {formatDateTimeFr(query.data?.dateModifEffective ?? null)}
      </PanelFooter>
    </Panel>
  );
}

/** Liste vide PARTAGÉE : « aucun filtre posé ». Référence stable, pour ne pas invalider les memos. */
const EMPTY_FILTER: readonly string[] = [];

/** Intention de verrouillage en attente de confirmation (toujours au grain FOURNISSEUR). */
interface PendingLock {
  /** `all` passe par le verrouillage GLOBAL de l'API ; les autres par le verrouillage par sélection. */
  readonly kind: 'supplier' | 'selection' | 'all';
  readonly sirets: readonly string[];
  readonly lineCount: number;
  readonly title: string;
  readonly description: string;
}

/**
 * Patch d'état d'URL émis par la page (le routeur le fusionne dans les search params).
 *
 * LOT F1 (GATE_HUMAN 2026-09-21) : `fournisseur`, `profil` et `etat` portent une LISTE, encodée en
 * paramètre RÉPÉTÉ. Une liste VIDE fait disparaître la clé de l'URL (paramètre absent = tous).
 */
export interface NegotiationSearchPatch {
  readonly fournisseur?: readonly string[] | undefined;
  readonly profil?: readonly string[] | undefined;
  readonly etat?: readonly NegotiationEntryStateDto[] | undefined;
  readonly verrouillees?: boolean | undefined;
  readonly hist?: NegotiationHistoryFilterDto | undefined;
  readonly page?: number | undefined;
}

export interface NegotiationScreenProps {
  readonly campaignId: string;
  /** Filtre fournisseur, aux SIRET, multi-valeurs (état d'URL ; absent ou vide = tous). */
  readonly supplierSirets?: readonly string[] | undefined;
  /** Filtre profil, aux codes, multi-valeurs (état d'URL ; absent ou vide = tous). */
  readonly profileCodes?: readonly string[] | undefined;
  /** Filtre d'état, multi-valeurs (état d'URL ; absent ou vide = tous les états). */
  readonly states?: readonly NegotiationEntryStateDto[] | undefined;
  /** Case « Afficher les verrouillées » (état d'URL ; défaut `false` = liste bornée aux ouvertes). */
  readonly showLocked?: boolean | undefined;
  /** Onglet de l'historique (état d'URL ; défaut « Tous »). */
  readonly historyFilter?: NegotiationHistoryFilterDto | undefined;
  /** Page de l'historique, 1-indexée (état d'URL ; défaut 1). */
  readonly historyPage?: number | undefined;
  /** Écrit l'état d'URL (merge sur les search params existants). */
  readonly onChangeSearch: (patch: NegotiationSearchPatch) => void;
}

export function NegotiationScreen({
  campaignId,
  supplierSirets,
  profileCodes,
  states,
  showLocked,
  historyFilter,
  historyPage,
  onChangeSearch,
}: NegotiationScreenProps): React.JSX.Element {
  // Listes NORMALISÉES : « absent » et « vide » sont le même état (aucun filtre). Référence stable
  // tant que l'URL ne change pas, pour ne pas invalider les memos en aval.
  const supplierFilter = React.useMemo(() => supplierSirets ?? EMPTY_FILTER, [supplierSirets]);
  const profileFilter = React.useMemo(() => profileCodes ?? EMPTY_FILTER, [profileCodes]);
  const stateFilter = React.useMemo(
    () => states ?? (EMPTY_FILTER as readonly NegotiationEntryStateDto[]),
    [states],
  );
  const linesQuery = useNegotiationLines(campaignId);
  const lockSelection = useLockNegotiationSelection(campaignId);
  const lockAll = useLockNegotiation(campaignId);

  const lines = React.useMemo(
    () => linesQuery.data?.lines ?? [],
    [linesQuery.data],
  );

  const locked = showLocked === true;
  const filters = React.useMemo(
    () => ({
      supplierSirets: supplierFilter,
      profileCodes: profileFilter,
      states: stateFilter,
      showLocked: locked,
    }),
    [supplierFilter, profileFilter, stateFilter, locked],
  );
  const displayed = React.useMemo(() => filterLines(lines, filters), [lines, filters]);

  // « Attente 48 h+ » est un renfort d'affichage horodaté. L'instant de référence est l'horodatage de
  // la DERNIÈRE lecture réussie (TanStack Query) : stable entre deux rendus — deux cellules d'une même
  // page ne peuvent pas se contredire — et rafraîchi à chaque invalidation, sans horloge locale lue à
  // chaque rendu. `0` tant qu'aucune donnée n'est arrivée (la liste est alors vide).
  const nowMs = linesQuery.dataUpdatedAt;

  const indicators = React.useMemo(
    () => buildIndicators(lines, nowMs),
    [lines, nowMs],
  );
  const progress = React.useMemo(() => buildProgress(lines), [lines]);
  /** Nombre de lignes PAR FOURNISSEUR : ce que verrouille réellement un bouton de ligne. */
  const lineCountBySiret = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const line of lines) {
      counts.set(line.supplierSiret, (counts.get(line.supplierSiret) ?? 0) + 1);
    }
    return counts;
  }, [lines]);
  const supplierOpts = React.useMemo(() => supplierOptions(lines), [lines]);
  const profileOpts = React.useMemo(() => profileOptions(lines), [lines]);

  // --- État de session in-memory (jamais localStorage : RULE_REF_006) ------------------------------
  const [selectedKeys, setSelectedKeys] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [detailSiret, setDetailSiret] = React.useState<string | null>(null);
  const [pendingLock, setPendingLock] = React.useState<PendingLock | null>(null);

  const toggleSelected = React.useCallback((key: string): void => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Lignes SÉLECTIONNABLES = lignes AFFICHÉES non verrouillées (une négociation close n'a plus rien à
  // verrouiller ; la cocher ne produirait qu'un NO-OP rapporté).
  const selectableKeys = React.useMemo(
    () => displayed.filter((line) => !line.lockedFinal).map(lineKey),
    [displayed],
  );
  const selectedLines = React.useMemo(
    () => lines.filter((line) => selectedKeys.has(lineKey(line))),
    [lines, selectedKeys],
  );
  const selectedSirets = React.useMemo(
    () => distinctSirets(selectedLines),
    [selectedLines],
  );
  /** Toutes les lignes des fournisseurs sélectionnés — ce qui basculera réellement (grain fournisseur). */
  const impactedBySelection = React.useMemo(
    () => lines.filter((line) => selectedSirets.includes(line.supplierSiret)),
    [lines, selectedSirets],
  );

  const allSelectableChecked =
    selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.has(key));
  const someSelectableChecked =
    !allSelectableChecked && selectableKeys.some((key) => selectedKeys.has(key));
  const selectAllRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (selectAllRef.current !== null) {
      selectAllRef.current.indeterminate = someSelectableChecked;
    }
  }, [someSelectableChecked]);

  const clearSelection = React.useCallback((): void => {
    setSelectedKeys(new Set());
  }, []);

  // --- Intentions de verrouillage (TOUJOURS au grain fournisseur) ----------------------------------
  const askLockSupplier = React.useCallback(
    (line: NegotiationLineDto): void => {
      const label = supplierLabelOf(line);
      const impacted = lineCountBySiret.get(line.supplierSiret) ?? 1;
      setPendingLock({
        kind: 'supplier',
        sirets: [line.supplierSiret],
        lineCount: impacted,
        title: `Verrouiller la négociation de ${label} ?`,
        description: `Le verrouillage agit sur le FOURNISSEUR : les ${plural(impacted, 'ligne')} de ${label} passeront à « Verrouillé », tous niveaux de séniorité confondus. ${LOCK_EFFECT_SENTENCE}`,
      });
    },
    [lineCountBySiret],
  );

  const askLockSelection = React.useCallback((): void => {
    setPendingLock({
      kind: 'selection',
      sirets: selectedSirets,
      lineCount: impactedBySelection.length,
      title: 'Verrouiller la sélection ?',
      description: `Le verrouillage agit sur le FOURNISSEUR : ${plural(selectedSirets.length, 'fournisseur')} sélectionnés, soit ${plural(impactedBySelection.length, 'ligne')} au total, passeront à « Verrouillé ». ${LOCK_EFFECT_SENTENCE}`,
    });
  }, [selectedSirets, impactedBySelection]);

  const askLockAll = React.useCallback((): void => {
    setPendingLock({
      kind: 'all',
      sirets: indicators.openSirets,
      lineCount: indicators.openLineCount,
      title: 'Verrouiller toutes les négociations ouvertes ?',
      description: `${plural(indicators.openSupplierCount, 'fournisseur')} encore ouverts, soit ${plural(indicators.openLineCount, 'ligne')}, passeront à « Verrouillé ». ${LOCK_EFFECT_SENTENCE}`,
    });
  }, [indicators]);

  const confirmLock = React.useCallback((): void => {
    if (pendingLock === null) {
      return;
    }
    const done = (): void => {
      setPendingLock(null);
      clearSelection();
    };
    if (pendingLock.kind === 'all') {
      lockAll.mutate(undefined, { onSuccess: done });
      return;
    }
    // Verrouillage PAR SÉLECTION même pour une seule ligne : c'est la surface qui pré-vérifie le lot
    // entier avant d'écrire et qui refuse un verrouillage non traçable (409, lacune d'audit comblée
    // en N2). Une ligne = son fournisseur, donc un lot d'un seul SIRET.
    lockSelection.mutate(pendingLock.sirets, { onSuccess: done });
  }, [pendingLock, lockAll, lockSelection, clearSelection]);

  const lockPending = lockSelection.isPending || lockAll.isPending;
  const lockError = lockSelection.error ?? lockAll.error;
  const lockResult = lockSelection.data;

  // --- Libellés des filtres (état dit EN TEXTE, jamais par la seule couleur — WCAG 1.4.1) ----------
  const supplierTrigger = filterTriggerLabel(
    'Fournisseur',
    supplierFilter.map(
      (siret) => supplierOpts.find((o) => o.value === siret)?.label ?? siret,
    ),
  );
  const profileTrigger = filterTriggerLabel(
    'Profil',
    profileFilter.map((code) => profileOpts.find((o) => o.value === code)?.label ?? code),
  );
  const stateTrigger = filterTriggerLabel(
    'État',
    stateFilter.map((value) => NEGOTIATION_STATE_LABEL[value]),
  );

  const detailLine =
    detailSiret === null
      ? undefined
      : lines.find((line) => line.supplierSiret === detailSiret);

  return (
    <div className="space-y-6">
      {/* L'<h1> de la page est l'intitulé de la campagne, rendu UNE fois par la barre de zone. */}
      <h2 className="sr-only">Négociation</h2>

      {/* Ligne des 4 indicateurs (sans card) + verrouillage global à droite. */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <section
          aria-label="Indicateurs de la négociation"
          className="min-w-0 flex-1 lg:flex-none"
        >
          {linesQuery.isPending ? (
            <p role="status" className="text-sm text-muted-foreground">
              Chargement des indicateurs…
            </p>
          ) : linesQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Erreur de chargement : {linesQuery.error.message}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-x-10 gap-y-4 sm:grid-cols-4">
              <Indicator
                label="Négociations ouvertes"
                value={String(indicators.openLineCount)}
                hint={`${plural(indicators.openSupplierCount, 'fournisseur')} · ${plural(indicators.openProfileCount, 'profil')}`}
              />
              <Indicator
                label="Re-saisies reçues"
                value={`${String(indicators.resaisiCount)} / ${String(indicators.totalLineCount)}`}
                hint={
                  indicators.lastResaisiAt === null
                    ? 'aucune re-saisie'
                    : `dernière ${formatDayTimeFr(indicators.lastResaisiAt)}`
                }
              />
              <Indicator
                label="En attente"
                value={String(indicators.pendingCount)}
                emphasis={indicators.pendingOver48hCount > 0}
                hint={
                  indicators.pendingOver48hCount > 0
                    ? `${String(indicators.pendingOver48hCount)} depuis plus de 48 h`
                    : 'aucune au-delà de 48 h'
                }
              />
              <Indicator
                label="Verrouillées"
                value={String(indicators.lockedLineCount)}
                hint={
                  indicators.lastLockedAt === null
                    ? 'aucun verrouillage'
                    : formatDayTimeFr(indicators.lastLockedAt)
                }
              />
            </div>
          )}
        </section>

        <Button
          type="button"
          variant="outline"
          className="shrink-0 border-destructive text-destructive hover:bg-destructive/10"
          disabled={indicators.openSupplierCount === 0 || lockPending}
          // Le verrouillage est au grain FOURNISSEUR : le nom accessible compte des fournisseurs ET
          // les lignes qui basculent, là où le libellé visible reste celui de la maquette.
          aria-label={`Verrouiller les négociations ouvertes : ${plural(indicators.openSupplierCount, 'fournisseur')}, ${plural(indicators.openLineCount, 'ligne')}`}
          onClick={askLockAll}
        >
          Verrouiller toutes les ouvertes ({String(indicators.openSupplierCount)})
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-6">
          <Panel
            headingId="negotiation-list-heading"
            title={
              <>
                Négociations
                <span className="font-mono text-sm font-normal text-muted-foreground">
                  · {String(indicators.openLineCount)} ouvertes ·{' '}
                  {String(indicators.lockedLineCount)} verrouillées
                </span>
              </>
            }
            aside={
              <span>
                Ouvrir une négociation : vue opérationnelle, page Fournisseurs
              </span>
            }
          >
            <PanelToolbar>
              {/* Trois listes autofiltrantes MULTI-valeurs (lot F1). L'option « Tous » disparaît :
                  ne rien cocher EST « tous », et « Effacer » y revient depuis le panneau. */}
              <FilterCombo
                triggerLabel={supplierTrigger}
                groupLabel="Fournisseur"
                options={supplierOpts.map((option) => ({
                  value: option.value,
                  label: option.label,
                  hint: option.value,
                }))}
                selected={supplierFilter}
                onChange={(next) => {
                  onChangeSearch({ fournisseur: next });
                }}
                searchPlaceholder="Rechercher un fournisseur…"
              />

              <FilterCombo
                triggerLabel={profileTrigger}
                groupLabel="Profil"
                options={profileOpts.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                selected={profileFilter}
                onChange={(next) => {
                  onChangeSearch({ profil: next });
                }}
                searchPlaceholder="Rechercher un profil…"
              />

              <FilterCombo
                triggerLabel={stateTrigger}
                groupLabel="État de la négociation"
                options={NEGOTIATION_STATE_ORDER.map((value) => ({
                  value,
                  label: NEGOTIATION_STATE_LABEL[value],
                }))}
                selected={stateFilter}
                onChange={(next) => {
                  // L'échelle d'état est FERMÉE : ce que rend la liste est forcément un des 4 états.
                  onChangeSearch({ etat: next as readonly NegotiationEntryStateDto[] });
                }}
                searchPlaceholder="Rechercher un état…"
              />

              {/* Chip « État · ouvertes × » : rappel VISIBLE du bornage par défaut de la liste ; la
                  croix fait exactement ce que fait la case « Afficher les verrouillées ». */}
              {!locked ? (
                <button
                  type="button"
                  onClick={() => {
                    onChangeSearch({ verrouillees: true });
                  }}
                  aria-label="Retirer le filtre : afficher aussi les négociations verrouillées"
                  className="inline-flex items-center gap-1 rounded-full border border-foreground bg-foreground px-2.5 py-1 text-xs font-semibold text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  État · ouvertes <span aria-hidden="true">×</span>
                </button>
              ) : null}
              {/* UNE chip par état coché (le filtre est multi-valeurs depuis le lot F1) : chaque
                  croix ne retire QUE son état, la sélection restante est préservée. */}
              {stateFilter.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    onChangeSearch({
                      etat: stateFilter.filter((current) => current !== value),
                    });
                  }}
                  aria-label={`Retirer le filtre d’état : ${NEGOTIATION_STATE_LABEL[value]}`}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  État · {NEGOTIATION_STATE_LABEL[value]} <span aria-hidden="true">×</span>
                </button>
              ))}

              <div className="flex items-center gap-2">
                <input
                  id="nego-show-locked"
                  type="checkbox"
                  className="size-4 rounded border-input"
                  checked={locked}
                  onChange={(event) => {
                    onChangeSearch({
                      verrouillees: event.target.checked ? true : undefined,
                    });
                  }}
                />
                <Label htmlFor="nego-show-locked" className="cursor-pointer font-normal">
                  Afficher les verrouillées
                </Label>
              </div>
            </PanelToolbar>

            <p className="px-6 pt-3 text-xs text-muted-foreground">
              Un fournisseur ne voit que ses binômes ouverts ; la re-saisie remplace sa valeur C2.
            </p>

            {/* Indicateur de progression DBU l.1267 — grain FOURNISSEUR, phrase conservée. */}
            <p
              role="status"
              data-testid="negotiation-progress"
              className="px-6 pb-3 pt-2 text-sm font-medium"
            >
              {String(progress.awaitingSuppliers)} fournisseur
              {progress.awaitingSuppliers > 1 ? 's' : ''} en attente de modification sur{' '}
              {String(progress.authorizedSuppliers)} autorisé
              {progress.authorizedSuppliers > 1 ? 's' : ''}.
            </p>

            {/* Barre de sélection (fond sombre) : n'apparaît qu'avec une sélection. */}
            {selectedLines.length > 0 ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-border bg-foreground px-6 py-3 text-background">
                <p
                  role="status"
                  data-testid="negotiation-selection-count"
                  className="text-xs font-semibold"
                >
                  {plural(selectedLines.length, 'négociation sélectionnée', 'négociations sélectionnées')}{' '}
                  · {selectedSirets.map((siret) => supplierLabelOf(
                    selectedLines.find((line) => line.supplierSiret === siret) ?? {
                      supplierSiret: siret,
                    },
                  )).join(', ')}{' '}
                  ·{' '}
                  {[...new Set(selectedLines.map((line) => NEGOTIATION_STATE_LABEL[line.state]))].join(
                    ', ',
                  )}
                </p>
                <p className="text-xs">
                  Le verrouillage agit au grain fournisseur : les{' '}
                  {plural(impactedBySelection.length, 'ligne')} de{' '}
                  {plural(selectedSirets.length, 'fournisseur')} basculeront en « Verrouillé ».
                </p>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-background hover:bg-background/15 hover:text-background"
                    onClick={clearSelection}
                  >
                    Désélectionner
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-background bg-transparent text-background hover:bg-background/15 hover:text-background"
                    disabled={lockPending}
                    onClick={askLockSelection}
                  >
                    Verrouiller la sélection ({plural(selectedSirets.length, 'fournisseur')})
                  </Button>
                </div>
              </div>
            ) : null}

            {lockError !== null ? (
              <p role="alert" className="px-6 py-3 text-sm text-destructive">
                {lockError.message}
              </p>
            ) : null}
            {lockResult !== undefined && !lockPending ? (
              <p role="status" className="px-6 py-3 text-xs text-muted-foreground">
                {plural(lockResult.locked.length, 'négociation verrouillée', 'négociations verrouillées')}
                {lockResult.alreadyLocked.length > 0
                  ? ` · ${plural(lockResult.alreadyLocked.length, 'fournisseur déjà verrouillé', 'fournisseurs déjà verrouillés')}`
                  : ''}
                {lockResult.withoutNegotiation.length > 0
                  ? ` · ${plural(lockResult.withoutNegotiation.length, 'fournisseur sans négociation ouverte', 'fournisseurs sans négociation ouverte')}`
                  : ''}
                .
              </p>
            ) : null}

            {linesQuery.isPending ? (
              <PanelMessage kind="status">Chargement des négociations…</PanelMessage>
            ) : linesQuery.isError ? (
              <PanelMessage kind="alert">
                Erreur de chargement : {linesQuery.error.message}
              </PanelMessage>
            ) : lines.length === 0 ? (
              <PanelMessage kind="status">
                Aucune négociation ouverte. Les négociations s’ouvrent depuis la vue
                opérationnelle, page Fournisseurs, dans le détail du fournisseur.
              </PanelMessage>
            ) : displayed.length === 0 ? (
              <PanelMessage kind="status">
                Aucune négociation ne correspond aux filtres courants.
              </PanelMessage>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="ds-table min-w-full table-fixed border-collapse"
                  style={{ width: TABLE_WIDTH }}
                >
                  <caption className="sr-only">
                    Négociations de la campagne, une ligne par fournisseur, profil et niveau de
                    séniorité : TJM C2 figé, valeur re-saisie, écart, prix cible, date d’ouverture,
                    état et action. Triées par fournisseur, puis profil et niveau.
                  </caption>
                  <colgroup>
                    <col style={{ width: SELECTION_WIDTH }} />
                    {COLUMNS.map((column) => (
                      <col key={column.id} style={{ width: column.width }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr className="border-b border-border">
                      <th scope="col" className={HEAD_CELL}>
                        {/* L'en-tête porte un nom accessible même sans libellé visible. */}
                        <span className="sr-only">Sélection</span>
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          className="size-3.5 rounded border-input"
                          aria-label="Sélectionner toutes les négociations ouvertes affichées"
                          checked={allSelectableChecked}
                          disabled={selectableKeys.length === 0}
                          onChange={(event) => {
                            setSelectedKeys(
                              event.target.checked ? new Set(selectableKeys) : new Set(),
                            );
                          }}
                        />
                      </th>
                      {COLUMNS.map((column) => (
                        <th
                          key={column.id}
                          scope="col"
                          className={cn(
                            HEAD_CELL,
                            column.numeric === true ? 'text-right' : 'text-left',
                          )}
                          // L'ordre est celui de l'API (fournisseur, puis profil et niveau) :
                          // l'écran l'ANNONCE, il ne le recalcule pas.
                          {...(column.id === 'fournisseur'
                            ? { 'aria-sort': 'ascending' as const }
                            : {})}
                        >
                          {column.label}
                          {column.id === 'fournisseur' ? (
                            <span aria-hidden="true"> ↑</span>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayed.map((line) => {
                      const key = lineKey(line);
                      const label = supplierLabelOf(line);
                      const impacted = lineCountBySiret.get(line.supplierSiret) ?? 1;
                      const awaiting = isAwaitingOver48h(line, nowMs);
                      return (
                        <tr
                          key={key}
                          className={cn(
                            'border-b border-border last:border-0',
                            line.lockedFinal ? 'bg-muted/30 text-dash-muted-row' : '',
                          )}
                        >
                          <td className={CELL}>
                            <input
                              type="checkbox"
                              className="size-3.5 rounded border-input"
                              aria-label={
                                line.lockedFinal
                                  ? `Négociation déjà verrouillée : ${label} ${coupleLabel(line)}`
                                  : `Sélectionner ${label} ${coupleLabel(line)}`
                              }
                              disabled={line.lockedFinal}
                              checked={selectedKeys.has(key)}
                              onChange={() => {
                                toggleSelected(key);
                              }}
                            />
                          </td>
                          <th scope="row" className={`${TEXT_CELL} text-left font-medium`}>
                            <span title={`${label} — ${line.supplierSiret}`}>{label}</span>
                          </th>
                          <td className={TEXT_CELL}>
                            <span title={line.profileLabel ?? line.profileCode}>
                              {line.profileCode}
                            </span>
                          </td>
                          <td className={TEXT_CELL}>{seniorityLabel(line.seniorityLevel)}</td>
                          <td className={NUM_CELL}>{formatEurAmount(line.tjmC2Eur)}</td>
                          <td className={NUM_CELL}>{formatEurAmount(line.tjmResaisiEur)}</td>
                          <td className={NUM_CELL}>
                            <EcartCell ecart={line.ecartEur} />
                          </td>
                          <td className={NUM_CELL}>{formatEurAmount(line.targetPriceEur)}</td>
                          <td className={`${CELL} whitespace-nowrap tabular-nums`}>
                            {formatDayTimeFr(line.reopenedAt)}
                          </td>
                          <td className={CELL}>
                            <Badge
                              variant="outline"
                              className={cn(
                                // Pas de `whitespace-nowrap` : la table est `table-fixed`, un libellé
                                // insécable déborderait sur la colonne Action.
                                'max-w-full whitespace-normal break-words px-1.5 text-left text-[10px] leading-tight',
                                NEGOTIATION_STATE_BADGE_CLASS[line.state],
                              )}
                            >
                              {NEGOTIATION_STATE_LABEL[line.state]}
                            </Badge>
                            {awaiting ? (
                              <span className="mt-1 block text-[10px] font-medium leading-tight text-dash-critical-fg">
                                Attente 48 h+
                              </span>
                            ) : null}
                          </td>
                          <td className={CELL}>
                            {line.lockedFinal ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                aria-expanded={detailSiret === line.supplierSiret}
                                aria-controls={DETAIL_PANEL_ID}
                                aria-label={`Détail de la négociation verrouillée de ${label}`}
                                onClick={() => {
                                  setDetailSiret((prev) =>
                                    prev === line.supplierSiret ? null : line.supplierSiret,
                                  );
                                }}
                              >
                                Détail
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 border-destructive px-2 text-xs text-destructive hover:bg-destructive/10"
                                disabled={lockPending}
                                aria-label={`Verrouiller la négociation de ${label} (${plural(impacted, 'ligne')})`}
                                onClick={() => {
                                  askLockSupplier(line);
                                }}
                              >
                                Verrouiller
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <PanelFooter>
              Écart = re-saisie − TJM C2. Sans re-saisie, le verrouillage conserve le TJM C2.
            </PanelFooter>
            <PanelFooter>
              Le verrouillage est définitif ; il est journalisé et visible dans Audit.
            </PanelFooter>
          </Panel>

          {/* Détail d'une négociation verrouillée (région STABLE, cible d'aria-controls). */}
          <div id={DETAIL_PANEL_ID} aria-live="polite">
            {detailSiret !== null ? (
              <NegotiationDetailPanel
                campaignId={campaignId}
                supplierSiret={detailSiret}
                supplierLabel={
                  detailLine === undefined ? detailSiret : supplierLabelOf(detailLine)
                }
              />
            ) : null}
          </div>
        </div>

        <aside
          aria-label="Historique de la négociation"
          className="min-w-0 xl:sticky xl:top-6 xl:self-start"
        >
          <NegotiationHistoryPanel
            campaignId={campaignId}
            filter={historyFilter ?? 'Tous'}
            page={historyPage ?? 1}
            onChangeFilter={(value) => {
              // Changer d'onglet REVIENT à la page 1 : garder la page afficherait une page vide.
              onChangeSearch({
                hist: value === 'Tous' ? undefined : value,
                page: undefined,
              });
            }}
            onChangePage={(value) => {
              onChangeSearch({ page: value <= 1 ? undefined : value });
            }}
          />
        </aside>
      </div>

      <ConfirmDialog
        open={pendingLock !== null}
        title={pendingLock?.title ?? ''}
        description={pendingLock?.description ?? ''}
        confirmLabel={lockPending ? 'Verrouillage…' : 'Verrouiller'}
        cancelLabel="Annuler"
        confirmDisabled={lockPending}
        onConfirm={confirmLock}
        onCancel={() => {
          setPendingLock(null);
        }}
      />
    </div>
  );
}
